import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import AvatarEditor from "react-avatar-editor";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { uploadFile } from "../../helpers/API";

const CircularImagePicker = ({
  value,
  onChange,
  defaultImage,
  size = 96,
  alt = "Profile",
  disabled = false,
  uploading = false,
  editLabel = "Change photo",
  modalTitle = "Edit photo",
  rotateLeftLabel = "Rotate left",
  zoomLabel = "Zoom",
  cancelLabel = "Cancel",
  applyLabel = "Apply",
  uploadingLabel = "Uploading...",
  onUploadSuccess,
  onUploadError,
  onSave,
}) => {
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [rotate, setRotate] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const displayImage = value || defaultImage;
  const isBusy = uploading || isUploading;

  const openFilePicker = () => {
    if (disabled || isBusy) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    setSelectedFile(file);
    setScale(1.2);
    setRotate(0);
    setIsModalOpen(true);
  };

  const handleCloseModal = (force = false) => {
    if (isUploading && !force) return;
    setIsModalOpen(false);
    setSelectedFile(null);
    setScale(1.2);
    setRotate(0);
    setIsUploading(false);
  };

  const handleApply = async () => {
    if (!editorRef.current || !selectedFile) return;

    setIsUploading(true);
    try {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));

      if (!blob) {
        throw new Error("Failed to process image");
      }

      const formData = new FormData();
      formData.append("file", blob, "profile.jpg");
      const res = await uploadFile(formData);
      const imageUrl = res?.data?.url || res?.url || res?.data || "";

      if (!imageUrl) {
        throw new Error("Upload failed");
      }

      if (onSave) {
        await onSave(imageUrl);
      } else {
        onChange(imageUrl);
        onUploadSuccess?.(imageUrl);
      }

      handleCloseModal(true);
    } catch (error) {
      console.error("Image upload error:", error);
      onUploadError?.(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRotate = () => {
    setRotate((prev) => prev - 90);
  };

  return (
    <>
      <div
        className="position-relative d-inline-block"
        style={{ width: size, height: size }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <button
          type="button"
          className="border-0 bg-transparent p-0 rounded-circle position-relative"
          onClick={openFilePicker}
          disabled={disabled || isBusy}
          style={{ width: size, height: size, cursor: disabled || isBusy ? "not-allowed" : "pointer" }}
          aria-label={editLabel}
        >
          <img
            src={displayImage}
            alt={alt}
            className="rounded-circle img-thumbnail"
            style={{
              width: size,
              height: size,
              objectFit: "cover",
              opacity: isBusy ? 0.6 : 1,
            }}
          />
          <span
            className="position-absolute top-0 start-0 w-100 h-100 rounded-circle d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              opacity: isBusy ? 0.7 : isHovered ? 1 : 0,
              transition: "opacity 0.2s ease",
              pointerEvents: "none",
            }}
          >
            {isBusy ? (
              <Spinner size="sm" color="light" />
            ) : (
              <i className="ri-camera-line text-white fs-4" />
            )}
          </span>
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleFileChange}
          disabled={disabled || isBusy}
          tabIndex={-1}
        />
      </div>

      <Modal isOpen={isModalOpen} toggle={handleCloseModal} centered size="lg">
        <ModalHeader toggle={handleCloseModal}>{modalTitle}</ModalHeader>
        <ModalBody>
          {selectedFile && (
            <div className="d-flex flex-column align-items-center gap-3">
              <AvatarEditor
                ref={editorRef}
                image={selectedFile}
                width={280}
                height={280}
                border={40}
                borderRadius={160}
                scale={scale}
                rotate={rotate}
              />

              <div className="w-100" style={{ maxWidth: 360 }}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <Button color="light" size="sm" onClick={handleRotate} disabled={isUploading}>
                    <i className="ri-anticlockwise-line me-1" />
                    {rotateLeftLabel}
                  </Button>
                </div>

                <label className="form-label mb-1">{zoomLabel}</label>
                <input
                  type="range"
                  className="form-range"
                  min="1"
                  max="3"
                  step="0.01"
                  value={scale}
                  onChange={(e) => setScale(parseFloat(e.target.value))}
                  disabled={isUploading}
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal} disabled={isUploading}>
            {cancelLabel}
          </Button>
          <Button color="primary" onClick={handleApply} disabled={isUploading}>
            {isUploading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {uploadingLabel}
              </>
            ) : (
              applyLabel
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

CircularImagePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultImage: PropTypes.string.isRequired,
  size: PropTypes.number,
  alt: PropTypes.string,
  disabled: PropTypes.bool,
  uploading: PropTypes.bool,
  editLabel: PropTypes.string,
  modalTitle: PropTypes.string,
  rotateLeftLabel: PropTypes.string,
  zoomLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  applyLabel: PropTypes.string,
  uploadingLabel: PropTypes.string,
  onUploadSuccess: PropTypes.func,
  onUploadError: PropTypes.func,
  onSave: PropTypes.func,
};

export default CircularImagePicker;
